import { AquaFishModel } from "../c/AquaFishModel";
import { ISpecies } from "../api/models/ISpecies";

/**
 * The species a project simulates. Snapshotted from the species table the way an
 * area snapshots its driver set, so a saved .fins does not depend on the backend
 * list still holding that row, or holding it under the same name.
 */
interface IProjectSpecies {
  name: string;
  modelCode: number;
}

interface IProject {
  /** System UUID that project is based on. */
  systemUUID: string | null;
  /** Name of system that project is based on. */
  systemName: string | null;
  name: string;
  structureDepth: number;
  stocking: number;
  clipping: number;
  // Absent on projects saved before the field existed; absent and null both mean
  // Atlantic salmon, which is what every simulation ran at the time, so older
  // .fins files need no migration. Read it through speciesModelCodeFor().
  species?: IProjectSpecies | null;
}

/**
 * The growth-model code a project's simulations run with. Defaults to the
 * Atlantic salmon feed table -- the only model FINS ever called before the
 * species could be chosen.
 */
const speciesModelCodeFor = (project: IProject): number =>
  project.species?.modelCode ?? AquaFishModel.AQUAFISHFTATLANTICSALMON;

/**
 * Display name of a project's species. Prefers the live species list so a
 * renamed species shows its current name, then the project's own snapshot (which
 * is all a .fins carries), then the default the simulation would actually run.
 */
/**
 * The culture defaults for a project's species, read live from the species table
 * rather than snapshotted: the values are effectively static, and reading them
 * live means a correction reaches every project instead of only new ones. What
 * *is* snapshotted is which species the project runs, so a saved .fins always
 * reproduces the same model.
 *
 * Every field is optional. A missing species row, or a species with no defaults,
 * leaves the facades on their own defaults, which is the behaviour that existed
 * before this table had the columns.
 */
const speciesDefaultsFor = (project: IProject, species: ISpecies[]) => {
  const s = species.find(x => x.modelCode === speciesModelCodeFor(project));
  return {
    initialWeight: s?.initialWeight ?? null,
    culturePeriod: s?.culturePeriod ?? null,
    // Reference only -- no growth model reads this.
    harvestWeight: s?.harvestWeight ?? null,
  };
}

const speciesNameFor = (project: IProject, species: ISpecies[]): string =>
  species.find(s => s.modelCode === speciesModelCodeFor(project))?.name
  ?? project.species?.name
  ?? "Atlantic salmon";

const DefaultProject: IProject = {
  systemUUID: null,
  systemName: null,
  name: "New project",
  structureDepth: 1,
  stocking: 50000,
  clipping: 1.0001
}

export { IProject, IProjectSpecies, DefaultProject, speciesModelCodeFor, speciesNameFor, speciesDefaultsFor }

